import { useLayoutEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import MarketingSiteHeader from './components/MarketingSiteHeader';
import marketingHeaderNavIsCurrent from './marketingHeaderNavIsCurrent';
import { seedVarietyProductDisplayTitle } from './seedVarietyCatalog';
import { useSeedVarietyProducts } from './useSeedVarietyProducts';

const DEFAULT_HERO = '/media/category-hero-default.png';

/** Premium card fallback when a variety has no image (ripe tomato, agri-tech feel). */
const TOMATO_PRODUCT_FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1592924357228-91a4daadc109?auto=format&fit=crop&w=960&q=80';

function specValueAccentClass(labelRaw) {
  const label = String(labelRaw || '').toLowerCase();
  if (label.includes('color')) return 'text-[#c81e1e] font-semibold tabular-nums';
  if (label.includes('yield')) return 'text-emerald-700 font-semibold tabular-nums';
  if (label.includes('maturity')) return 'text-emerald-800 font-semibold tabular-nums';
  if (label.includes('sowing')) return 'text-emerald-700/90 font-medium tabular-nums';
  return 'font-medium text-neutral-800 tabular-nums';
}

function ArrowUpRight({ className }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M4 12L12 4M12 4H6M12 4V10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function cardHeadline(product, firstVariant) {
  const vName = typeof firstVariant?.name === 'string' ? firstVariant.name.trim() : '';
  if (vName) return vName;
  const label = typeof product?.familyLabel === 'string' ? product.familyLabel.trim() : '';
  if (label) return label;
  return seedVarietyProductDisplayTitle(product);
}

function safeDecodeURIComponent(segment) {
  if (segment == null || typeof segment !== 'string') return '';
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function prettifyCategoryParam(raw) {
  if (!raw || typeof raw !== 'string') return 'Category';
  const s = safeDecodeURIComponent(raw.trim());
  if (!s) return 'Category';
  return s
    .split(/[-_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** One row per variant for spec cards (product lines can hold multiple varieties). */
function useCategoryVariantRows(filteredProducts) {
  return useMemo(() => {
    const rows = [];
    for (const product of filteredProducts) {
      const vars = Array.isArray(product.variants) ? product.variants : [];
      if (vars.length === 0) {
        rows.push({ product, variant: null });
        continue;
      }
      for (const variant of vars) {
        rows.push({ product, variant });
      }
    }
    return rows;
  }, [filteredProducts]);
}

/** Hero → wave → product image grid (seed varieties in this category). Data from GET /api/site. */
export default function CropCategoryPage() {
  const { categoryId: categoryIdParam } = useParams();
  const categoryId = categoryIdParam ? safeDecodeURIComponent(categoryIdParam).trim() : '';
  const { site, products, categories, ready } = useSeedVarietyProducts();

  const categoryMeta = useMemo(
    () => (categoryId ? categories.find((c) => c.id === categoryId) : null),
    [categories, categoryId]
  );

  const filteredProducts = useMemo(() => {
    if (!categoryId) return [];
    return products.filter((p) => p.categoryId === categoryId);
  }, [products, categoryId]);

  const categoryTitle = categoryMeta?.name || filteredProducts[0]?.categoryName || prettifyCategoryParam(categoryId);

  const heroImage = useMemo(() => {
    const fromCategory = categoryMeta?.heroImage && String(categoryMeta.heroImage).trim();
    if (fromCategory) return fromCategory;
    for (const p of filteredProducts) {
      const v = p.variants?.[0];
      if (v?.image && String(v.image).trim()) return String(v.image).trim();
    }
    return DEFAULT_HERO;
  }, [categoryMeta, filteredProducts]);

  const categoryVariantRows = useCategoryVariantRows(filteredProducts);

  useLayoutEffect(() => {
    const prev = typeof document !== 'undefined' ? document.title : '';
    document.title = `${categoryTitle} · Rafid Seeds`;
    return () => {
      document.title = prev;
    };
  }, [categoryTitle]);

  if (!ready) {
    return (
      <div className="min-h-screen bg-white text-neutral-900">
        <MarketingSiteHeader
          site={site}
          idPrefix="crops"
          alwaysSolid
          isNavLinkCurrent={marketingHeaderNavIsCurrent}
        />
        <div className="flex min-h-[50vh] items-center justify-center text-neutral-500 pt-[calc(4.5rem+env(safe-area-inset-top))]">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900">
      <MarketingSiteHeader
        site={site}
        idPrefix="crops"
        alwaysSolid={false}
        isNavLinkCurrent={marketingHeaderNavIsCurrent}
      />

      <section className="relative">
        <div className="relative isolate min-h-[min(52vh,560px)] overflow-hidden bg-brand-forest">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-gradient-to-b from-brand-forest/75 via-brand-forest/55 to-brand-forest/80"
            aria-hidden
          />
          <div className="relative z-10 mx-auto flex max-w-shell flex-col items-center justify-center px-4 pb-16 pt-[calc(5rem+env(safe-area-inset-top))] text-center sm:px-6 sm:pb-20 sm:pt-[calc(5.5rem+env(safe-area-inset-top))] md:pb-24 md:pt-[calc(6rem+env(safe-area-inset-top))]">
            <nav
              className="mb-5 text-[0.8125rem] font-medium text-white/90 sm:text-sm"
              aria-label="Breadcrumb"
            >
              <ol className="m-0 flex list-none flex-wrap items-center justify-center gap-x-2 gap-y-1 p-0">
                <li>
                  <Link to="/" className="text-white/95 no-underline transition-colors hover:text-white">
                    Home
                  </Link>
                </li>
                <li className="text-white/50" aria-hidden>
                  /
                </li>
                <li>
                  <Link
                    to="/seed-varieties"
                    className="text-white/95 no-underline transition-colors hover:text-white"
                  >
                    Crops
                  </Link>
                </li>
                <li className="text-white/50" aria-hidden>
                  /
                </li>
                <li className="text-brand-lime-soft font-semibold">{categoryTitle}</li>
              </ol>
            </nav>
            <h1 className="max-w-4xl text-4xl font-bold tracking-tight text-white text-balance sm:text-5xl md:text-6xl">
              {categoryTitle}
            </h1>
            {categoryMeta?.summary ? (
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85 sm:text-lg">{categoryMeta.summary}</p>
            ) : null}
          </div>
        </div>
        <div className="relative z-[1] -mt-px leading-[0] text-white" aria-hidden>
          <svg
            className="block h-12 w-full sm:h-16 md:h-[4.25rem]"
            viewBox="0 0 1440 56"
            preserveAspectRatio="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="currentColor"
              d="M0,32 C180,8 360,52 540,28 C720,4 900,48 1080,26 C1220,10 1320,22 1440,18 L1440,56 L0,56 Z"
            />
          </svg>
        </div>
      </section>

      <main className="mx-auto max-w-shell px-4 pb-16 pt-2 sm:px-6 sm:pb-20 lg:px-8">
        {filteredProducts.length === 0 ? (
          <p className="mt-8 text-center text-neutral-600">
            No products in this category yet.{' '}
            
          </p>
        ) : (
          <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-7">
            {filteredProducts.map((product) => {
              const first = product.variants?.[0];
              const headline = cardHeadline(product, first);
              const categoryLabel = (product.categoryName && String(product.categoryName).trim()) || categoryTitle;
              const subline =
                categoryLabel && categoryLabel.toLowerCase() !== headline.toLowerCase() ? categoryLabel : '';
              const src = first?.image && String(first.image).trim() ? String(first.image).trim() : '';
              const alt = first?.alt || headline;

              return (
                <li key={product.id}>
                  <Link
                    to={`/seed-varieties/${encodeURIComponent(product.id)}`}
                    aria-label={`${headline}${subline ? `, ${subline}` : ''} — view variety`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-neutral-200/70 bg-brand-card-soft/90 shadow-sm no-underline ring-1 ring-black/[0.03] transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-md"
                  >
                    <div className="aspect-[4/3] w-full shrink-0 overflow-hidden bg-neutral-100">
                      {src ? (
                        <img
                          src={src}
                          alt={alt}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                          loading="lazy"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center px-4 text-center text-sm font-semibold text-neutral-500">
                          {headline}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-1 flex-col items-center px-4 pb-5 pt-4 text-center sm:px-5 sm:pt-5">
                      <h2 className="text-[1.05rem] font-bold leading-snug text-brand-forest sm:text-lg">{headline}</h2>
                      {subline ? (
                        <p className="mt-1.5 text-xs font-bold uppercase tracking-wide text-brand-lime sm:text-[0.8125rem]">
                          {subline}
                        </p>
                      ) : null}
                      <span
                        className="mt-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-brand-forest shadow-sm ring-1 ring-neutral-200/90 transition-colors group-hover:bg-brand-lime-soft group-hover:text-brand-forest"
                        aria-hidden
                      >
                        <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </main>

      {filteredProducts.length > 0 ? (
        <section
          className="border-t border-neutral-200/80 bg-gradient-to-b from-[#faf9f7] via-brand-cream/70 to-[#f3f6f3] py-14 sm:py-16 md:py-20"
          aria-labelledby="crop-category-varieties-heading"
        >
          <div className="mx-auto max-w-shell px-4 sm:px-6 lg:px-8">
            <h2
              id="crop-category-varieties-heading"
              className="mb-10 text-center text-lg font-bold uppercase tracking-[0.12em] text-brand-forest sm:mb-12 sm:text-xl md:text-2xl"
            >
              {categoryTitle} varieties
            </h2>

            <ul className="m-0 grid list-none grid-cols-1 gap-6 p-0 lg:grid-cols-2 lg:gap-8">
              {categoryVariantRows.map(({ product, variant }) => {
                const catLine = (product.categoryName && String(product.categoryName).trim()) || categoryTitle;
                const vName = variant?.name?.trim() || cardHeadline(product, product.variants?.[0]);
                const breeder = variant?.breeder?.trim();
                const specs = Array.isArray(variant?.specs) ? variant.specs : [];
                const img = variant?.image?.trim();
                const alt = variant?.alt?.trim() || vName;
                const to = variant?.id
                  ? `/seed-varieties/${encodeURIComponent(product.id)}/${encodeURIComponent(variant.id)}`
                  : `/seed-varieties/${encodeURIComponent(product.id)}`;

                const tomatoish =
                  catLine.toLowerCase().includes('tomato') || categoryTitle.toLowerCase().includes('tomato');
                const imageSrc = img || (tomatoish ? TOMATO_PRODUCT_FALLBACK_IMAGE : '');

                return (
                  <li key={variant?.id ? `${product.id}-${variant.id}` : product.id} className="flex">
                    <Link
                      to={to}
                      className="group flex w-full flex-col no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/40 focus-visible:ring-offset-2"
                    >
                      <div className="relative isolate overflow-hidden rounded-3xl shadow-[0_20px_50px_-20px_rgba(20,38,28,0.35)] ring-1 ring-black/[0.06] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_-18px_rgba(200,30,30,0.22)]">
                        <div className="relative h-[13.5rem] overflow-hidden bg-gradient-to-br from-red-950/30 via-neutral-900/10 to-emerald-950/40 sm:h-[15rem]">
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={alt}
                              className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full items-center justify-center bg-gradient-to-br from-neutral-200 to-neutral-300 px-6 text-center">
                              <span className="text-sm font-semibold text-neutral-600">{vName}</span>
                            </div>
                          )}
                          <div
                            className="absolute inset-0 bg-gradient-to-t from-brand-forest/85 via-brand-forest/20 to-transparent"
                            aria-hidden
                          />
                          <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6">
                            <div className="mb-2 inline-flex items-center gap-2 rounded-md bg-white/15 px-2.5 py-1 backdrop-blur-md">
                              <span className="text-[0.65rem] font-bold uppercase tracking-[0.22em] text-white/95">
                                {catLine.toUpperCase()}
                              </span>
                            </div>
                            <h3 className="max-w-xl text-2xl font-bold leading-tight tracking-tight text-white text-balance sm:text-[1.65rem]">
                              {vName}
                            </h3>
                            {breeder ? (
                              <p className="mt-2 text-sm text-white/85">
                                <span className="font-medium text-emerald-200/90">Breeder</span>{' '}
                                <span className="font-semibold text-white">{breeder}</span>
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="relative -mt-7 mx-4 mb-4 rounded-2xl border border-white/70 bg-white/90 px-4 py-5 shadow-[0_12px_40px_-16px_rgba(20,38,28,0.28)] backdrop-blur-xl sm:mx-5 sm:px-5 sm:py-6">
                          {specs.length > 0 ? (
                            <div className="overflow-hidden rounded-xl border border-neutral-200/80 bg-gradient-to-b from-white to-neutral-50/80">
                              {specs.map((row, i) => (
                                <div
                                  key={`${row.label}-${row.value}-${i}`}
                                  className={`flex flex-col gap-1 px-3.5 py-3 sm:flex-row sm:items-baseline sm:justify-between sm:gap-6 sm:px-4 ${
                                    i !== specs.length - 1 ? 'border-b border-neutral-200/70' : ''
                                  } ${i % 2 === 0 ? 'bg-white/50' : 'bg-emerald-50/20'}`}
                                >
                                  <span className="shrink-0 text-[0.7rem] font-bold uppercase tracking-[0.12em] text-neutral-500">
                                    {row.label}
                                  </span>
                                  <span
                                    className={`min-w-0 text-left text-[0.9375rem] leading-snug sm:max-w-[55%] sm:text-right ${specValueAccentClass(row.label)}`}
                                  >
                                    {row.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : product.summary ? (
                            <p className="text-sm leading-relaxed text-neutral-600">{product.summary}</p>
                          ) : null}

                          <div className="mt-5 flex justify-end border-t border-neutral-200/80 pt-4">
                            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#b91c1c] transition-colors group-hover:text-[#991b1b]">
                              View full specification sheet
                              <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-current bg-red-50/80 text-current transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5">
                                <ArrowUpRight className="h-4 w-4" />
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
}
